import React, { FC } from 'react';
import { useWindowDimensions, View } from 'react-native';
import { GridBox, scale } from '@components/frame';
import ColorWrapper from '@styles/colors';
import { ColorFunction } from '@styles/colors/types';
import { injectConditionalObject, isPhone, isWeb } from '@helpers';
import Picture from './components/Picture';
import ImageHandlingHeader from './components/Header/Header';
import { useManageImageHandling } from './hooks/useManageImageHandling';
import Thumbnail from './components/Thumbnail';
import MobileBottomTabs from './components/MobileBottomTabs';
import { IMAGE_HANDLING_HEADER_HEIGHT } from '@components/imageHandling/common';
import { HeaderIconKey } from './components/Header/components/HeaderIcons/types';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import DrawingCanvas from './components/DrawingCanvas';
import AnnotationsOverlay from './components/DrawingCanvas/components/AnnotationsOverlay';
import AnnotationOverlay from './components/DrawingCanvas/components/AnnotationOverlay';
import DrawingScope from './components/DrawingCanvas/DrawingScope';
import { useSelector } from 'react-redux';
import { selectCurrentUserId } from '@reducers/user';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';

interface ImageHandlingProps {
  C: ColorFunction;
  hide?: () => void; // This will be passed by Popup component
}

const ImageHandling: FC<ImageHandlingProps> = ({ C, hide }) => {
  const {
    cropImageDocument,
    removeImageDocument,
    rotateImageDocument,
    imagesDocuments,
    isSingleImage,
    imagesDocumentsLoading,
    mainContainerStyles,
    selectedIndex,
    setSelectedIndex,
    setMode,
    positions,
    imageContext,
    imageContextForAdding,
    showActions,
    setShowActions,
    triggerShowActions,
    groupedImages,
    groupedImagesKeys,
    thumbnailScrollRef,
    onImagePress,
    shouldDisplayCollapseIcon,
    isDrawing,
    triggerDraw,
    exitDrawing,
    shouldDisplayHeader,
    shouldDisplayBottomComponent,
    pictureContainerStyle,
    pictureHeight,
    pictureWidth,
  } = useManageImageHandling({ C });

  const activeImage = imagesDocuments[selectedIndex];
  const currentUserId = useSelector(selectCurrentUserId);

  const { width, height } = useWindowDimensions();

  const drawingActive = isDrawing && activeImage != null && currentUserId != null;

  const tree = (
    <View testID={TEST_IDS.imageHandlingPopup.root} style={mainContainerStyles}>
      <View style={[mainContainerStyles, { zIndex: positions.picture, backgroundColor: C('Background/Light/System') }]}>
        {shouldDisplayHeader && (
          <ImageHandlingHeader
            C={C}
            cropImage={cropImageDocument}
            rotateImage={rotateImageDocument}
            deleteImage={removeImageDocument}
            imageContextForAdding={imageContextForAdding}
            images={imagesDocuments}
            selectedIndex={selectedIndex}
            setSelectedIndex={setSelectedIndex}
            setMode={setMode}
            showActions={showActions || isWeb}
            shouldCenterDocTypeModal={isWeb || isPhone}
            onDismiss={hide}
            onMobileBackButtonPress={hide}
            onCollapseIconPress={hide}
            {...injectConditionalObject(!shouldDisplayCollapseIcon, { onClose: hide })}
            triggerDraw={triggerDraw}
            isDrawing={isDrawing}
            enabledIcons={[
              HeaderIconKey.CROP,
              HeaderIconKey.ROTATE,
              HeaderIconKey.DRAW,
              HeaderIconKey.SHARE,
              HeaderIconKey.ADD_IMAGE,
              HeaderIconKey.MORE,
              shouldDisplayCollapseIcon ? HeaderIconKey.COLLAPSE : HeaderIconKey.CLOSE,
            ]}
          />
        )}
        <Picture
          imageContext={imageContext}
          images={imagesDocuments}
          loaded={!imagesDocumentsLoading}
          selectedIndex={selectedIndex}
          setSelectedIndex={setSelectedIndex}
          isSingleImage={isSingleImage}
          triggerShowActions={triggerShowActions}
          showActions={showActions || isWeb}
          onImagePress={onImagePress}
          containerStyle={pictureContainerStyle}
          {...(pictureHeight != null ? { height: pictureHeight } : {})}
          {...(pictureWidth != null ? { width: pictureWidth } : {})}
          withNotchTop
          withoutAttachItems={isWeb || isPhone || !showActions}
          // Native annotation surface lives inside Zoomable via this slot; web renders the surface
          // inline in DrawingCanvas's drawing surface.
          zoomableOverlay={drawingActive && !isWeb ? <AnnotationOverlay insideZoomable /> : undefined}
        />
        {shouldDisplayBottomComponent && (
          <MobileBottomTabs
            C={C}
            images={imagesDocuments}
            rotateImage={rotateImageDocument}
            selectedIndex={selectedIndex}
            setMode={setMode}
            deleteImage={removeImageDocument}
            setSelectedIndex={setSelectedIndex}
            cropImage={cropImageDocument}
            triggerDraw={triggerDraw}
            isDrawing={isDrawing}
          />
        )}
      </View>
      <View style={[mainContainerStyles, { zIndex: positions.thumbnail }]}>
        <Thumbnail
          C={C}
          groupedImages={groupedImages}
          groupedImagesKeys={groupedImagesKeys}
          setMode={setMode}
          setSelectedIndex={setSelectedIndex}
          setShowActions={setShowActions}
          thumbnailScrollRef={thumbnailScrollRef}
          onBackButtonPress={hide}
        />
      </View>
      {!isDrawing && activeImage != null && (
        <View
          style={[
            { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: positions.picture + 1 },
            pictureContainerStyle,
          ]}
          pointerEvents="none"
        >
          <AnnotationsOverlay image={activeImage} pageNumber={activeImage.pageNumber ?? 1} />
        </View>
      )}
      {drawingActive && (
        <View
          style={{
            position: 'absolute',
            top: isWeb ? scale(IMAGE_HANDLING_HEADER_HEIGHT) : 0,
            left: 0,
            width,
            height: height - (isWeb ? scale(IMAGE_HANDLING_HEADER_HEIGHT) : 0),
            zIndex: positions.picture + 10,
          }}
          pointerEvents="box-none"
        >
          <DrawingCanvas />
        </View>
      )}
    </View>
  );

  const renderTree = () =>
    isDrawing && activeImage != null && currentUserId != null ? (
      <DrawingScope image={activeImage} pageNumber={activeImage.pageNumber ?? 1} currentUserId={currentUserId} onExit={exitDrawing}>
        {tree}
      </DrawingScope>
    ) : (
      tree
    );

  // If opened via Popup modal, wrap with necessary providers
  if (hide) {
    return (
      <GestureHandlerRootView style={{ flex: 1 }}>
        <SafeAreaProvider>{renderTree()}</SafeAreaProvider>
      </GestureHandlerRootView>
    );
  }

  // Normal navigation mode
  return (
    <GridBox
      {...{
        backgroundColor: C('Background/Light/System'),
        headerOffset: isWeb,
        customHeight: height,
        customWidth: width,
      }}
    >
      {renderTree()}
    </GridBox>
  );
};

export default ColorWrapper(ImageHandling);
